/**
 * Числа баланса УЛЬЯ. ЕДИНСТВЕННОЕ место в коде, где живут игровые константы.
 *
 * Назначение файла: перенести таблицы из docs/economy/06-formulas.md в код один раз,
 * чтобы ни сервер, ни клиент не держали своих копий (docs/tech/01-architecture.md).
 * Любое изменение числа здесь обязано сопровождаться правкой Э6 и записью в журнал
 * версий ТЗ — иначе документация и код разъезжаются (.cursor/rules/20-balance-and-code.mdc).
 *
 * Импорты: типы предметной области из ./types.
 * Единицы: значения в ☾ (как в ТЗ), перевод в milli делают формулы.
 *
 * Источник: docs/economy/06-formulas.md (v0.4)
 */

import { type Crystals, type HexKind, type SiegeTarget, type TraceQuality } from "./types.js";

// ─────────────────────────────────────────────────────────────────────────────
// Э6.1. Соглашения
// ─────────────────────────────────────────────────────────────────────────────

/** Час — базовый тик начисления дохода соты. */
export const INCOME_TICK_HOURS = 1;

/** Экономические сутки начинаются в 04:00 по часовому поясу Очага. */
export const ECONOMIC_DAY_START_HOUR = 4;

/** Ночь: с 23:00 до 08:00 новые осады не объявляются (Э6.6). */
export const NIGHT_START_HOUR = 23;
export const NIGHT_END_HOUR = 8;

// ─────────────────────────────────────────────────────────────────────────────
// Э6.2. Герой
// ─────────────────────────────────────────────────────────────────────────────

export const HERO = {
  healthBase: 200,
  healthPerLevel: 25,
  healthPerFifthLevel: 50,
  manaBase: 80,
  manaPerLevel: 12,
  manaPerFifthLevel: 20,
  attackBase: 8,
  attackPerLevel: 1,
  mightPerLevel: 12,
  mightPerSpellLevel: 3,
  mightPerFifthLevel: 10,
  /**
   * База кривой опыта. Изменение v1.6: 100 → 45 при той же степени.
   * При базе 100 щит новичка (снимается на уровне 8) держался пять недель вместо
   * заявленной недели, а 50-й уровень требовал около 33 месяцев (Э6.2, Э7.10).
   */
  xpBase: 45,
  xpExponent: 1.8,
  /** Потолок уровня героя (§12.7 docs/07-hero-progression.md). */
  levelCap: 50,
  /** Усиление урона за каждое улучшение книги заклинаний. */
  spellbookBonusPerUpgrade: 0.08,
  /** Затухание вклада участников рейда по очереди: 0.55^(i-1). */
  raidContributionDecay: 0.55,
  /**
   * Стойкость — доля снижения входящего урона (Э6.2, новое в v1.6).
   * Единственная ручка, которой длительность боя приводится к закону 4 «арена 60-90 секунд»:
   * без неё пятеро с фокусом огня выносили сторону за 39-45 секунд. Рост с уровнем нарочно
   * почти нулевой, иначе рейд слабых на кита перестаёт работать (§13.3).
   */
  toughnessBase: 0.4,
  toughnessPerLevel: 0.001,
  toughnessMax: 0.45,
} as const;

/** Темп боя на арене (Э6.2, БОЙ). */
export const COMBAT = {
  /** Интервал автоатаки в секундах: она идёт сама, игрок жмёт только заклинания. */
  autoAttackIntervalSeconds: 2,
  /** Границы длительности арены из закона 4. */
  arenaSecondsMin: 60,
  arenaSecondsMax: 90,
  /** Сразу после боя возвращается доля максимума здоровья и маны (§12.5). */
  afterBattleRestoreShare: 0.3,
} as const;

/**
 * Величины эффектов заклинаний, которые не наносят урон (Э6.2, ЭФФЕКТЫ).
 * Доли считаются от максимального здоровья цели, длительности — в секундах.
 */
export const SPELL_EFFECTS = {
  shieldAbsorbShare: 0.35,
  shieldSeconds: 6,
  honeyHealShare: 0.25,
  frostSlowShare: 0.35,
  frostSeconds: 4,
  frostRingRadius: 1,
  rootsSeconds: 2.5,
  wrathStunSeconds: 1.5,
  chainSparkNeighbors: 2,
  chainSparkNeighborShare: 0.5,
  fireballHaloShare: 0.4,
} as const;

/** Перезарядки заклинаний в секундах (§15.1). */
export const SPELL_COOLDOWNS = {
  lightningSeconds: 4,
  fireballSeconds: 6,
  shieldSeconds: 12,
  frostRingSeconds: 10,
  hexStormSeconds: 18,
  honeySeconds: 8,
  rootsSeconds: 11,
  skyWrathSeconds: 40,
  chainSparkSeconds: 9,
} as const;

/** Стоимость заклинаний в мане (§15.1). */
export const SPELL_MANA = {
  lightning: 18,
  fireball: 24,
  shield: 22,
  frostRing: 20,
  hexStorm: 40,
  honey: 26,
  roots: 22,
  skyWrath: 70,
  chainSpark: 28,
} as const;

/** Коэффициенты урона заклинаний (§15.3). */
export const SPELL_COEFFICIENTS = {
  lightning: 1.0,
  fireball: 0.9,
  hexStorm: 0.65,
  chainSpark: 0.8,
  skyWrath: 1.2,
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Э6.3. Доход соты
// ─────────────────────────────────────────────────────────────────────────────

/** Базовый доход соты в ☾ за сутки по типу (Э6.3). */
export const BASE_INCOME_PER_DAY: Record<HexKind, Crystals> = {
  ordinary: 18,
  grove: 14,
  academy: 16,
  shrine: 24,
  market: 30,
  crossroad: 36,
  sanctuary: 0,
} as const;

export const INCOME = {
  /** Множитель, если сота потеряла связь с Очагом. */
  disconnectedMultiplier: 0.3,
  /** Прибавка за каждый уровень укрепления (0..3 → 1.00..1.15). */
  fortificationStep: 0.05,
  /** Прибавка за каждого чужого жильца в соте. */
  foreignHearthStep: 0.02,
  /** Потолок множителя жильцов. */
  foreignHearthCap: 1.2,
  /** Доля держателя клановой соты; остальное уходит в склад. */
  clanHolderShare: 0.3,
  /** Множитель дохода «раненой» соты после боя. */
  woundedMultiplier: 0.5,
  /** Штраф за растянутость: 5% за каждую соту сверх порога. */
  overreachStep: 0.05,
  /** Порог, после которого начинается штраф за растянутость. */
  overreachThreshold: 12,
  /** Максимальный штраф за растянутость. */
  overreachCap: 0.4,
  /** Казна соты копится не дольше 48 часов дохода. */
  vaultCapHours: 48,
} as const;

/**
 * Затухание дохода при отсутствии игрока (Э6.3, kАктивность).
 * Границы включительные: до 3 суток — полный доход, после 14 — ноль.
 */
export const ACTIVITY_DECAY: ReadonlyArray<{ maxDays: number; multiplier: number }> = [
  { maxDays: 3, multiplier: 1.0 },
  { maxDays: 7, multiplier: 0.6 },
  { maxDays: 14, multiplier: 0.3 },
  { maxDays: Number.POSITIVE_INFINITY, multiplier: 0.0 },
] as const;

/** Запустение брошенной земли (docs/economy/02-income.md). */
export const ABANDON = {
  /** Сутки без входа: метка «брошена», доход ноль. */
  markDays: 15,
  /** Сутки без входа: земля ничья, казна сгорает (кроме соты с Очагом). */
  desolationDays: 21,
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Э6.4. Очаг и оброк с жильцов
// ─────────────────────────────────────────────────────────────────────────────

export const HEARTH = {
  /** Минимальный интервал переноса Очага, сутки (§Т3.1). */
  moveCooldownDays: 14,
  /** Суточный доход Очага при входе в игру, ☾. */
  dailyBase: 20,
  /** Повышенный доход под щитом новичка, ☾. */
  dailyUnderShield: 30,
  /** Прибавка, если Очаг стоит в роще, ☾. */
  groveBonus: 3,
  /** Прибавка, если земля под Очагом своя или клановая, ☾. */
  ownLandBonus: 5,
  /** Потолок оброка с жильцов, ☾ за сутки. */
  lodgerTributeCap: 10,
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Э6.5. Дань
// ─────────────────────────────────────────────────────────────────────────────

/** Ставка дани в ☾ за сутки за одну соту по типу (Э6.5). */
export const TRIBUTE_RATE: Record<HexKind, Crystals> = {
  grove: 0,
  sanctuary: 0,
  ordinary: 3,
  academy: 4,
  market: 6,
  shrine: 6,
  crossroad: 8,
} as const;

/** Множитель дани по качеству Следа: Отголосок бесплатен (Э6.5). */
export const TRACE_QUALITY_MULTIPLIER: Record<TraceQuality, number> = {
  echo: 0,
  true: 1.0,
  deep: 1.5,
  anchor: 2.0,
} as const;

/** Множитель дани по отношению к владельцу (Э6.5). */
export const RELATION_MULTIPLIER = {
  self: 0,
  ally: 0,
  neutral: 1.0,
  enemy: 1.25,
} as const;

export const TRIBUTE = {
  /** Дань за проход по форпосту вполовину. */
  outpostMultiplier: 0.5,
  /** Прибавка за каждый уровень героя выше первого. */
  levelStep: 0.02,
  /** Скидка гостю под щитом новичка. */
  newbieShieldMultiplier: 0.25,
  /** Прибавка за каждый уровень укрепления соты. */
  fortificationStep: 0.05,
  /** Суточный потолок дани: база плюс уровень. */
  dailyCapBase: 20,
  /** Суточный потолок дани под щитом новичка, ☾. */
  dailyCapUnderShield: 10,
  /** Возраст аккаунта, до которого проход бесплатный, часов. */
  freeAccountAgeHours: 72,
  /** Уровень героя, до которого проход бесплатный. */
  freeBelowLevel: 3,
  /** Деление уплаченной дани: личная сота владельца. */
  splitPersonal: { owner: 0.7, clanVault: 0.15, burn: 0.15 },
  /** Деление уплаченной дани: клановая сота. */
  splitClan: { holder: 0.15, clanVault: 0.7, burn: 0.15 },
  /** Срок, в который недоимка может быть переведена владельцу, сутки (Э3.7). */
  arrearsClaimDays: 30,
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Э6.6. Осада
// ─────────────────────────────────────────────────────────────────────────────

/** Ставка осады в ☾ по виду цели (Э6.6). */
/** Ритуал на пустую соту (Э4.2, тихий ритуал). Ставка — SIEGE_STAKE.empty. */
export const RITUAL = {
  manaCost: 20,
} as const;

export const SIEGE_STAKE: Record<SiegeTarget, Crystals> = {
  empty: 10,
  contestedEmpty: 10,
  ordinary: 20,
  grove: 20,
  academy: 20,
  market: 30,
  crossroad: 45,
  shrine: 45,
  hearthWeakening: 25,
  clanHq: 60,
} as const;

/**
 * Множитель навязчивости по числу осад за сутки (Э6.6).
 * Индекс — количество уже объявленных осад: пятая и далее стоят в 2.5 раза дороже.
 */
export const PUSHINESS_MULTIPLIER: ReadonlyArray<number> = [1.0, 1.25, 1.5, 2.0, 2.5] as const;

export const SIEGE = {
  /** Трофей при автобое — половина казны. */
  autoBattleTrophyShare: 0.5,
  /** Награда за победу в осаде, ☾. */
  attackerWinReward: 20,
  /** Награда за успешную защиту, ☾. */
  defenderWinReward: 25,
  /** Доля долгового лимита, выше которой осады запрещены. */
  debtBlockShare: 0.5,
  /** Распределение ставки по исходам (Э6.6). */
  outcomes: {
    attackerWin: { attacker: 1.0 },
    defenderWin: { defender: 0.7, clanVault: 0.15, burn: 0.15 },
    draw: { attacker: 0.5, defender: 0.25, burn: 0.25 },
    cancelled: { attacker: 0.75, burn: 0.25 },
  },
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Э6.7. Долг
// ─────────────────────────────────────────────────────────────────────────────

export const DEBT = {
  /** Лимит долга: база плюс за уровень, ☾. */
  limitBase: 50,
  limitPerLevel: 15,
  /** Пеня до половины лимита. */
  penaltyRateLow: 0.015,
  /** Пеня после половины лимита. */
  penaltyRateHigh: 0.02,
  /** Доля лимита, разделяющая ступени «недоимка» и «тень долга». */
  shadowShare: 0.5,
  /** Списание за принудительные торги при аресте: трое суток дохода соты. */
  auctionChargeDays: 3,
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Э6.8. Разовые начисления и опыт
// ─────────────────────────────────────────────────────────────────────────────

export const AWARDS = {
  /** Работа улья: ☾ за соту, максимум 5 сот в сутки. */
  hiveWork: 2,
  hiveWorkDailyLimit: 5,
  captureEmpty: 8,
  siegeWin: 20,
  defenceWin: 25,
  /** Оплачиваемых защит в сутки не больше трёх. */
  defenceWinDailyLimit: 3,
  dailyQuests: [10, 12, 15] as const,
  weeklyQuest: 60,
  hearthTutorial: 50,
  seasonReward: { min: 150, max: 600 },
} as const;

export const XP = {
  trueTrace: 15,
  deepTrace: 10,
  captureEmpty: 40,
  siegeWinAttack: 60,
  siegeWinDefence: 45,
  dailyQuest: 12,
  weeklyQuest: 80,
  siegeLoss: 8,
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Э6.9. Магазин
// ─────────────────────────────────────────────────────────────────────────────

/** Цены лавки улья в ☾ (Э6.9). */
export const HIVE_SHOP_PRICES = {
  potion: 25,
  traceWick: 40,
  scarRepair: 30,
  fortification: [60, 120, 200] as const,
  quarterEye: 120,
  bannerEconomy: 150,
} as const;

/** Цены кассы в ✦ (Э6.9). */
export const CASH_SHOP_PRICES = {
  heroSkin: [300, 900] as const,
  bannerAura: [150, 400] as const,
  spellShell: [120, 250] as const,
  seasonPass: 800,
  potion: 40,
  traceWick: 60,
  hiveEye: 120,
} as const;

/** Курс конверсии: 1 ✦ даёт 3 ☾, обратно невозможно (Э6.9). */
export const CONVERSION_RATE = 3;

// ─────────────────────────────────────────────────────────────────────────────
// Э6.10. Клан
// ─────────────────────────────────────────────────────────────────────────────

export const CLAN = {
  /** Содержание кластеров: ☾ в сутки за каждую соту сверх порога. */
  upkeepPerHex: 2,
  upkeepFreeHexes: 15,
  /** Деление дохода клановой соты. */
  incomeSplit: { vault: 0.7, holder: 0.3 },
  /** Деление трофея рейда, плюс 10% в склад сверху. */
  raidTrophySplit: { initiator: 0.4, participants: 0.6, vaultExtra: 0.1 },
  /** Деление ставки рейда. */
  raidStakeSplit: { initiator: 0.5, participants: 0.5 },
  /** Лидер может вывести из склада не больше 20% в сутки. */
  vaultWithdrawShare: 0.2,
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Э6.12. Слава: сезонный рейтинг
// ─────────────────────────────────────────────────────────────────────────────

/** Начисления славы за экономические сутки (Э6.12). */
export const GLORY = {
  perHexAtDayEnd: 1,
  /** Больше тридцати сот в зачёт суток не идёт: рейтинг не про размер, а про игру. */
  hexesCountedPerDay: 30,
  perDefenceWin: 12,
  perAttackWin: 10,
  perRitualCapture: 3,
  perTrueTrace: 1,
  tracesCountedPerDay: 5,
  /** Явка в рейде считается даже при поражении: иначе клан не собирается. */
  perRaidAppearance: 4,
  perHexLost: 3,
  /** Кап суток: рейтинг не должен быть соревнованием по количеству свободного времени. */
  dailyCap: 60,
} as const;

/** Лиги по славе сезона (Э6.12). Пороги идут по возрастанию. */
export const GLORY_LEAGUES = [
  { league: "vagrant", from: 0 },
  { league: "neighbour", from: 250 },
  { league: "householder", from: 600 },
  { league: "blockLord", from: 1400 },
  { league: "districtLord", from: 2800 },
] as const;

// ─────────────────────────────────────────────────────────────────────────────
// §9.1. Туман войны
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Правила рассеивания тумана (docs/04-map-and-geo.md §9.1).
 * Радиусы — в кольцах H3 от соты игрока.
 */
export const FOG = {
  /** Верный След открывает саму соту как разведанную, а кольцо 1 — как силуэт. */
  traceSilhouetteRings: 1,
  /** Полевое заклинание «Прокол тумана». */
  piercingRings: 2,
  /** Око Улья: платный силуэт всего города. */
  eyeOfHiveHours: 24,
  /** Через сколько суток без посещения «разведано» затухает до «силуэта». */
  scoutedDecayDays: 30,
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Карта: лимиты кадра, присутствие, подсказка казны (docs/tech/02-api-events.md §Т2.2)
// ─────────────────────────────────────────────────────────────────────────────

/** Лимиты выдачи карты и округление присутствия (§7.3, закон 4). */
/** Длительности отклика интерфейса (docs/23-visual-and-feedback.md §23.3). */
export const UI_ANIMATION = {
  /** Заливка соты от центра к границам после ритуала. */
  captureHexMs: 1500,
} as const;

export const MAP = {
  /** Максимум сот в одном ответе /map/hexes — как предохранитель веб-клиента (docs/tech/05-web-client.md). */
  maxHexesInFrame: 4000,
  /**
   * Границы масштаба, который принимает /map/hexes. MapLibre отдаёт зум дробным
   * (жест щипком на телефоне даёт 14.19), поэтому сервер проверяет диапазон,
   * а не целость числа: иначе каждый кадр после щипка получал бы validation.failed.
   */
  zoomMin: 0,
  zoomMax: 24,
  /** Меньше этого числа присутствие не показываем: точное «1–2 человека» раскрывает координаты. */
  presenceMinDisplay: 3,
  /** Округление «около N» вверх до кратного шага. */
  presenceRoundStep: 3,
} as const;

/**
 * Пороги подсказки казны для чужой соты (docs/economy/02-income.md).
 * Доля заполнения потолка казны, не абсолютные ☾.
 */
export const VAULT_HINT = {
  emptyMaxShare: 0.2,
  mediumMaxShare: 0.55,
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Э1.2. Кошелёк: конверсия ✦ → ☾
// ─────────────────────────────────────────────────────────────────────────────

/** Курс обмена кассы на игровые кристаллы (Э6.9, docs/economy/01-currencies.md). */
export const WALLET = {
  convertRate: 3,
} as const;

